import { useState, useEffect } from 'react';
import { supabase } from '../shared/supabase';
import {
  CreditCard, Crown, TrendingUp, Users, DollarSign,
  Search, RefreshCw, CheckCircle, XCircle, Clock,
  ChevronDown, ChevronUp, ExternalLink, AlertTriangle,
  Download, Filter
} from 'lucide-react';

const PaymentsManagement = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [stats, setStats] = useState({
    totalRevenue: 0,
    activeSubscribers: 0,
    totalSubscribers: 0,
    mrr: 0,
    conversionRate: 0,
    totalUsers: 0,
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('subscribers');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortField, setSortField] = useState('premium_since');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    loadAll();
  }, []);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const loadAll = async () => {
    setLoading(true);
    await Promise.all([loadSubscribers(), loadPurchases(), loadStats()]);
    setLoading(false);
  };

  const loadStats = async () => {
    try {
      // Total users
      const { count: totalUsers } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true });

      // Premium users
      const { data: premiumUsers, count: totalSubscribers } = await supabase
        .from('profiles')
        .select('is_premium, premium_since, stripe_subscription_id', { count: 'exact' })
        .eq('is_premium', true);

      // Active (with stripe subscription)
      const activeSubscribers = premiumUsers?.filter(u => u.stripe_subscription_id)?.length || 0;

      // Revenue from user_purchases
      const { data: purchaseData } = await supabase
        .from('user_purchases')
        .select('amount_paid');

      const totalRevenue = purchaseData?.reduce((sum, p) => sum + parseFloat(p.amount_paid || 0), 0) || 0;
      const mrr = activeSubscribers * 9.95;
      const conversionRate = totalUsers > 0 ? ((totalSubscribers / totalUsers) * 100).toFixed(1) : 0;

      setStats({ totalRevenue, activeSubscribers, totalSubscribers, mrr, conversionRate, totalUsers });
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const loadSubscribers = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, email, is_premium, premium_since, premium_expires_at, stripe_customer_id, stripe_subscription_id, created_at, total_sessions, current_streak')
        .order('premium_since', { ascending: false });

      if (error) throw error;
      setSubscribers(data || []);
    } catch (err) {
      console.error('Error loading subscribers:', err);
    }
  };

  const loadPurchases = async () => {
    try {
      const { data, error } = await supabase
        .from('user_purchases')
        .select('*, profiles(full_name, email)')
        .order('purchased_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      setPurchases(data || []);
    } catch (err) {
      console.error('Error loading purchases:', err);
    }
  };

  const handleTogglePremium = async (user) => {
    const action = user.is_premium ? 'revoke' : 'grant';
    if (!window.confirm(`${action === 'grant' ? 'Grant' : 'Revoke'} premium access for ${user.email}?`)) return;

    setActionLoading(user.id);
    try {
      const updates = user.is_premium
        ? { is_premium: false, premium_since: null, stripe_subscription_id: null }
        : { is_premium: true, premium_since: new Date().toISOString() };

      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id);

      if (error) throw error;
      showToast(`Premium ${action === 'grant' ? 'granted' : 'revoked'} for ${user.email}`);
      await loadAll();
      if (selectedUser?.id === user.id) setSelectedUser({ ...selectedUser, ...updates });
    } catch (err) {
      showToast('Failed to update user: ' + err.message, 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) { setSortDir(sortDir === 'asc' ? 'desc' : 'asc'); }
    else { setSortField(field); setSortDir('desc'); }
  };

  const exportCSV = () => {
    const rows = [
      ['Name', 'Email', 'Status', 'Premium Since', 'Stripe Customer ID', 'Total Sessions'],
      ...filteredSubscribers.map(u => [
        u.full_name || 'N/A',
        u.email,
        u.is_premium ? 'Premium' : 'Free',
        u.premium_since ? new Date(u.premium_since).toLocaleDateString() : 'N/A',
        u.stripe_customer_id || 'N/A',
        u.total_sessions || 0,
      ])
    ];
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'subscribers.csv'; a.click();
  };

  // Filter & sort
  const filteredSubscribers = subscribers
    .filter(u => {
      const matchSearch = !searchTerm ||
        u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.full_name?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchFilter =
        filterStatus === 'all' ? true :
        filterStatus === 'premium' ? u.is_premium :
        filterStatus === 'free' ? !u.is_premium :
        filterStatus === 'stripe' ? !!u.stripe_subscription_id : true;
      return matchSearch && matchFilter;
    })
    .sort((a, b) => {
      let va = a[sortField], vb = b[sortField];
      if (!va) return 1; if (!vb) return -1;
      if (typeof va === 'boolean') { va = va ? 1 : 0; vb = vb ? 1 : 0; }
      return sortDir === 'asc' ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1);
    });

  const SortIcon = ({ field }) => sortField === field
    ? (sortDir === 'asc' ? <ChevronUp className="w-3 h-3 inline ml-1" /> : <ChevronDown className="w-3 h-3 inline ml-1" />)
    : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <RefreshCw className="w-8 h-8 animate-spin text-purple-500 mr-3" />
        <span className="text-gray-600 font-medium">Loading payments data...</span>
      </div>
    );
  }

  return (
    <div className="payments-management">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-white text-sm font-medium transition-all ${toast.type === 'error' ? 'bg-red-500' : 'bg-green-500'}`}>
          {toast.type === 'error' ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payments & Subscriptions</h1>
          <p className="text-gray-500 text-sm mt-1">Monitor revenue, subscribers and manage access</p>
        </div>
        <div className="flex gap-2">
          <button onClick={loadAll} className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-all shadow-sm">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button onClick={exportCSV} className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-all shadow-sm">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={<DollarSign className="w-5 h-5 text-green-600" />} label="Total Revenue" value={`$${stats.totalRevenue.toFixed(2)}`} bg="bg-green-50" border="border-green-100" />
        <StatCard icon={<TrendingUp className="w-5 h-5 text-blue-600" />} label="MRR" value={`$${stats.mrr.toFixed(2)}`} bg="bg-blue-50" border="border-blue-100" sub="Monthly Recurring" />
        <StatCard icon={<Crown className="w-5 h-5 text-purple-600" />} label="Premium Users" value={stats.totalSubscribers} bg="bg-purple-50" border="border-purple-100" sub={`${stats.conversionRate}% conversion`} />
        <StatCard icon={<Users className="w-5 h-5 text-gray-600" />} label="Total Users" value={stats.totalUsers} bg="bg-gray-50" border="border-gray-200" sub={`${stats.activeSubscribers} with Stripe`} />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit mb-6">
        {[['subscribers', 'Subscribers'], ['transactions', 'Transactions']].map(([id, label]) => (
          <button key={id} onClick={() => setActiveTab(id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === id ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
            {label}
          </button>
        ))}
      </div>

      {activeTab === 'subscribers' && (
        <>
          {/* Filters */}
          <div className="flex gap-3 mb-4 flex-wrap">
            <div className="relative flex-1 min-w-48">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <select
                value={filterStatus}
                onChange={e => setFilterStatus(e.target.value)}
                className="pl-9 pr-8 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-300 appearance-none"
              >
                <option value="all">All Users ({subscribers.length})</option>
                <option value="premium">Premium ({subscribers.filter(u => u.is_premium).length})</option>
                <option value="free">Free ({subscribers.filter(u => !u.is_premium).length})</option>
                <option value="stripe">Has Stripe ({subscribers.filter(u => u.stripe_subscription_id).length})</option>
              </select>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <Th onClick={() => handleSort('full_name')}>User <SortIcon field="full_name" /></Th>
                    <Th>Status</Th>
                    <Th onClick={() => handleSort('premium_since')}>Premium Since <SortIcon field="premium_since" /></Th>
                    <Th>Stripe</Th>
                    <Th onClick={() => handleSort('total_sessions')}>Sessions <SortIcon field="total_sessions" /></Th>
                    <Th>Actions</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredSubscribers.length === 0 ? (
                    <tr><td colSpan={6} className="text-center py-12 text-gray-400">No users found</td></tr>
                  ) : filteredSubscribers.map(user => (
                    <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                            {(user.full_name || user.email || '?').charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">{user.full_name || 'No name'}</div>
                            <div className="text-gray-400 text-xs">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {user.is_premium
                          ? <span className="inline-flex items-center gap-1 bg-purple-100 text-purple-700 text-xs font-semibold px-2.5 py-1 rounded-full"><Crown className="w-3 h-3" /> Premium</span>
                          : <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-500 text-xs font-semibold px-2.5 py-1 rounded-full">Free</span>
                        }
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {user.premium_since ? new Date(user.premium_since).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                      </td>
                      <td className="px-4 py-3">
                        {user.stripe_subscription_id
                          ? <span className="inline-flex items-center gap-1 text-green-600 text-xs font-medium"><CheckCircle className="w-3.5 h-3.5" /> Active</span>
                          : user.stripe_customer_id
                          ? <span className="inline-flex items-center gap-1 text-amber-600 text-xs font-medium"><Clock className="w-3.5 h-3.5" /> Customer</span>
                          : <span className="text-gray-300 text-xs">—</span>
                        }
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-center">{user.total_sessions || 0}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleTogglePremium(user)}
                            disabled={actionLoading === user.id}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                              user.is_premium
                                ? 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200'
                                : 'bg-purple-50 text-purple-600 hover:bg-purple-100 border border-purple-200'
                            }`}
                          >
                            {actionLoading === user.id ? '...' : user.is_premium ? 'Revoke' : 'Grant'}
                          </button>
                          <button
                            onClick={() => setSelectedUser(user)}
                            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200 transition-all"
                          >
                            View
                          </button>
                          {user.stripe_customer_id && (
                            <a
                              href={`https://dashboard.stripe.com/customers/${user.stripe_customer_id}`}
                              target="_blank"
                              rel="noreferrer"
                              className="p-1.5 rounded-lg bg-gray-50 text-gray-400 hover:text-gray-600 hover:bg-gray-100 border border-gray-200 transition-all"
                              title="View in Stripe"
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-400">
              Showing {filteredSubscribers.length} of {subscribers.length} users
            </div>
          </div>
        </>
      )}

      {activeTab === 'transactions' && (
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
          {purchases.length === 0 ? (
            <div className="text-center py-16">
              <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-400 font-medium">No transactions yet</p>
              <p className="text-gray-300 text-sm mt-1">Purchases will appear here once users subscribe</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <Th>User</Th>
                    <Th>Amount</Th>
                    <Th>Date</Th>
                    <Th>Content</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {purchases.map(p => (
                    <tr key={p.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{p.profiles?.full_name || 'Unknown'}</div>
                        <div className="text-gray-400 text-xs">{p.profiles?.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-semibold text-green-600">${parseFloat(p.amount_paid).toFixed(2)}</span>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {new Date(p.purchased_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-xs font-mono">{p.content_id?.substring(0, 8)}...</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between text-xs text-gray-400">
                <span>{purchases.length} transactions</span>
                <span className="font-semibold text-gray-600">Total: ${purchases.reduce((s, p) => s + parseFloat(p.amount_paid || 0), 0).toFixed(2)}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* User Detail Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="bg-gradient-to-br from-purple-500 to-pink-500 px-6 py-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-white text-xl font-bold">
                  {(selectedUser.full_name || selectedUser.email || '?').charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="text-white font-bold">{selectedUser.full_name || 'No name'}</div>
                  <div className="text-white/70 text-sm">{selectedUser.email}</div>
                </div>
              </div>
              <button onClick={() => setSelectedUser(null)} className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white text-lg transition-colors">×</button>
            </div>
            <div className="p-6 space-y-4">
              <DetailRow label="Status" value={selectedUser.is_premium ? '👑 Premium' : '🆓 Free'} />
              <DetailRow label="Premium Since" value={selectedUser.premium_since ? new Date(selectedUser.premium_since).toLocaleDateString() : 'N/A'} />
              <DetailRow label="Expires" value={selectedUser.premium_expires_at ? new Date(selectedUser.premium_expires_at).toLocaleDateString() : 'N/A'} />
              <DetailRow label="Stripe Customer" value={selectedUser.stripe_customer_id || 'N/A'} mono />
              <DetailRow label="Stripe Subscription" value={selectedUser.stripe_subscription_id || 'N/A'} mono />
              <DetailRow label="Total Sessions" value={selectedUser.total_sessions || 0} />
              <DetailRow label="Current Streak" value={`${selectedUser.current_streak || 0} days`} />
              <DetailRow label="Member Since" value={new Date(selectedUser.created_at).toLocaleDateString()} />
            </div>
            <div className="px-6 pb-6 flex gap-3">
              <button
                onClick={() => { handleTogglePremium(selectedUser); setSelectedUser(null); }}
                className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                  selectedUser.is_premium
                    ? 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200'
                    : 'bg-purple-600 text-white hover:bg-purple-700'
                }`}
              >
                {selectedUser.is_premium ? 'Revoke Premium' : 'Grant Premium'}
              </button>
              {selectedUser.stripe_customer_id && (
                <a
                  href={`https://dashboard.stripe.com/customers/${selectedUser.stripe_customer_id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gray-100 text-gray-600 hover:bg-gray-200 text-sm font-semibold transition-all"
                >
                  <ExternalLink className="w-4 h-4" /> Stripe
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper components
const StatCard = ({ icon, label, value, bg, border, sub }) => (
  <div className={`${bg} border ${border} rounded-2xl p-4`}>
    <div className={`w-9 h-9 ${bg} rounded-xl flex items-center justify-center mb-3`}>{icon}</div>
    <div className="text-2xl font-bold text-gray-900">{value}</div>
    <div className="text-sm font-medium text-gray-600 mt-0.5">{label}</div>
    {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
  </div>
);

const Th = ({ children, onClick }) => (
  <th onClick={onClick} className={`px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider ${onClick ? 'cursor-pointer hover:text-gray-700 select-none' : ''}`}>
    {children}
  </th>
);

const DetailRow = ({ label, value, mono }) => (
  <div className="flex items-start justify-between gap-4">
    <span className="text-sm text-gray-500 flex-shrink-0">{label}</span>
    <span className={`text-sm font-medium text-gray-900 text-right break-all ${mono ? 'font-mono text-xs' : ''}`}>{value}</span>
  </div>
);

export default PaymentsManagement;